// Owns the async lifecycle of a Milkdown/Crepe editor builder.
//
// A Crepe builder is created asynchronously (`await builder.create()`). If the
// owner is torn down — or a newer mount supersedes this one — while that
// create() is still in flight, the half-built editor must be destroyed.
// Otherwise it finishes creating and mounts a second `<div class="milkdown">`
// into the (shared, persisting) DOM target, which is then leaked forever.
//
// EditorMount tracks the in-flight builder so unmount()/a superseding mount()
// can cancel it. Pure module: no DOM, no Stimulus — easy to unit-test.
//
// A builder is anything implementing:
//   create()  : Promise   // resolves once the editor is mounted
//   destroy() : void      // tears the editor down and removes its DOM

export class EditorMount {
    #buildBuilder
    #pending = null
    #active = null

    // buildBuilder constructs and configures a fresh builder (without creating
    // it). It is called once per mount().
    constructor(buildBuilder) {
        this.#buildBuilder = buildBuilder
    }

    get active() {
        return this.#active
    }

    // Build and create an editor. Resolves to the created builder if it is
    // still the current mount once create() finishes, or null if it was
    // unmounted or superseded meanwhile — in which case it has been destroyed.
    async mount() {
        const builder = this.#buildBuilder()
        this.#pending = builder
        await builder.create()
        if (this.#pending !== builder) {
            builder.destroy()
            return null
        }
        this.#pending = null
        this.#active = builder
        return builder
    }

    // Cancel any in-flight create() and destroy the active editor.
    unmount() {
        this.#pending = null
        const active = this.#active
        this.#active = null
        active?.destroy()
    }
}
